import Vue, { CreateElement } from 'src/vue'
import { Component } from 'vue-property-decorator'

import { TimeSwitcher } from '@src/elements/time-switch/time-switch'
import { Icon } from '@src/elements/icon/icon'
import ApiClient from '@src/lib/api_client'
import { getCurrentMonthPeriods } from '@src/util/functions'
import { API } from '@src/lib/api_path'
import { Subtitle1 } from '@src/elements/typography/typography'
import { convertYYYYMMDDToDate } from '@src/util/date-format'
import { DateFilterType } from '@src/elements/time-switch/date-filter-type'
import VueApexCharts from 'vue-apexcharts'

Vue.use(VueApexCharts)

Vue.component('apexchart', VueApexCharts)

import './subscriptions.sass'
import { CHART_LABEL_FONT_STYLE } from '@src/util/constants'

type NewMembersResponse = { periods: NewMembersPeriod[] }
type NewMembersPeriod = {
  from: string
  to: string
  new_members: number
}
type InactiveMembersResponse = { periods: InactiveMembersPeriod[] }
type InactiveMembersPeriod = {
  from: string
  to: string
  inactive_members: number
}

@Component
export class SubscriptionsStatistics extends Vue {

  private apiClient: ApiClient

  private newMembers: any[] = []

  private cancelledSubscriptions: any[] = []

  constructor () {
    super()
    this.apiClient = new ApiClient(API.TRAINERS_API.CLUB_STATS)
  }

  changeTimeFrame (timeFrame: string, periods: any[]) {
    return this.loadStats(periods)
  }

  getChartOpt (values: any[], chartStartColor: string, chartEndColor: string, circleColor: string) {
    let dateLabels = values.map(value => {
      return value.x
    })

    // add a marker to the last item in the list
    let formattedValues = values.map((value) => value.y)

    const series = [
      {
        data: formattedValues,
        color: circleColor
      }
    ]

    let options = {
      chart: {
        type: 'line',
        height: 80,
        width: 180,
        background: 'transparent',
        redrawOnParentResize: true,
        redrawOnWindowResize: true,
        zoom: {
          enabled: false
        },
        toolbar: {
          show: false
        }
      },
      grid: {
        show: true,
        xaxis: {
          lines: {
            show: false
          }
        }
      },
      dataLabels: {
        enabled: false
      },
      stroke: {
        width: 1.5,
        curve: 'smooth'
      },
      legend: {
        show: false
      },
      tooltip: {
        enabled: false
      },
      markers: {
        discrete: [
          {
            seriesIndex: 0,
            dataPointIndex: values.length - 1,
            fillColor: circleColor,
            strokeColor: circleColor,
            size: 4,
            shape: 'circle'
          }
        ]
      },
      fill: {
        type: 'gradient',
        gradient: {
          type: 'vertical',
          shadeIntensity: 1,
          opacityFrom: 1,
          opacityTo: 1,
          colorStops: [
            {
              offset: 0,
              color: chartStartColor,
              opacity: 1
            },
            {
              offset: 0.5,
              color: chartEndColor,
              opacity: 50
            },
            {
              offset: 100,
              color: chartEndColor,
              opacity: 1
            }
          ]
        }
      },
      yaxis: {
        show: false,
        opposite: true,
        tickAmount: 4,
        labels: {
          ...CHART_LABEL_FONT_STYLE.style,
          fontSize: '0.875rem'
        }
      },
      xaxis: {
        type: 'category',
        categories: dateLabels,
        tooltip: {
          enabled: false
        },
        labels: {
          show: false
        },
        axisBorder: {
          show: false
        },
        axisTicks: {
          show: false
        },
        crosshairs: {
          show: false,
          dropShadow: {
            enabled: true
          }
        }
      }
    }

    return { options, series }
  }

  render (h: CreateElement) {
    let newMembersCount = this.newMembers.reduce(function (prev, curr) {
      return prev + curr.y
    }, 0)

    let cancelledSubscriptionsCount = this.cancelledSubscriptions.reduce(function (prev, curr) {
      return prev + curr.y
    }, 0)

    // apex
    const { options: newMembersOptions, series: newMembersSeries } = this.getChartOpt(
      this.newMembers, '#1BDB99', '#21F4EE', '#29D9C1'
    )
    const { options: cancelledMembersOptions, series: cancelledMembersSeries } = this.getChartOpt(this.cancelledSubscriptions, '#A600FF', '#F551A7', '#F551A6')

    return <div class='column subscriptions'>
      <TimeSwitcher dateFilters={[DateFilterType.MONTH, DateFilterType.YEAR]} change={this.changeTimeFrame} align='right' allowFutureDate={false}/>

      <div class='row'>
        <div class='column'>
          <Subtitle1>{this.$t('PAGE.CLUB.CLUB_STATISTICS.NEW_MEMBERS')}</Subtitle1>
          <div class='count'>
            {newMembersCount !== 0 ? `+${newMembersCount}` : '0'}
            <Icon name='brand/new-members-arrow' />
            <div class='row chart-wrapper'>
              <apexchart
                height={newMembersOptions.chart.height}
                options={newMembersOptions} series={newMembersSeries}
              />
            </div>
          </div>
        </div>

        <div class='column'>
          <Subtitle1>{this.$t('PAGE.CLUB.CLUB_STATISTICS.CANCELLED_SUBSCRIPTION_MEMBERS')}</Subtitle1>
          <div class='count pink'>
            {cancelledSubscriptionsCount !== 0 ? `-${cancelledSubscriptionsCount}` : '0'}
            <Icon name='brand/cancelled-members-arrow' />
            <div class='row chart-wrapper'>
              <apexchart
                height={cancelledMembersOptions.chart.height}
                options={cancelledMembersOptions} series={cancelledMembersSeries}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  }

  beforeMount () {
    return this.loadStats(getCurrentMonthPeriods())
  }

  private loadStats (periods: any[]) {
    return Promise.all([this.loadNewMembers(periods), this.loadInactiveMembers(periods)])
  }

  private loadNewMembers (periods: any[]) {
    return this.apiClient.get<NewMembersResponse>(API.TRAINERS_API.CLUB_STATS_NEW_MEMBERS, { periods: periods })
      .then((response) => {
        this.newMembers = this.buildChartData(response.periods)
      })
      .catch((error) => console.error('failed to retrieve club stats - new members', error))
  }

  private loadInactiveMembers (periods: any[]) {
    return this.apiClient.get<InactiveMembersResponse>(API.TRAINERS_API.CLUB_STATS_INACTIVE_MEMBERS, { periods: periods })
      .then((response) => {
        this.cancelledSubscriptions = this.buildChartData(response.periods)
      })
      .catch((error) => console.error('failed to retrieve club stats - inactive members', error))
  }

  private buildChartData (periods) {
    // start the list from 0
    let firstDate = convertYYYYMMDDToDate(periods[0].from)
    firstDate.setDate(firstDate.getDate() - 1)
    let list = [{ x: firstDate, y: 0 }]

    // show the trend by only displaying the values different than 0
    periods.forEach(period => {
      if (period.new_members && period.new_members !== 0) {
        list.push({ x: new Date(period.from), y: period.new_members })
      } else if (period.inactive_members && period.inactive_members !== 0) {
        list.push({ x: new Date(period.from), y: period.inactive_members })
      }
    })

    return list
  }
}
